import {
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Platform,
} from "react-native";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BehavioralVerifyCheckBody,
  BehavioralVerifyItem,
  BehavioralVerifyResponse,
  CaptchaResponse,
} from "../../net/response";
import {
  behavioralVerifyCommit,
  behavioralVerifyGet,
  captchaChk,
  captchaGet,
} from "../../net/service";
import { hideLoading, showLoading } from "../../components/Loading";
import { showToast } from "../../components/Toast";
import {
  Str_BehavioralVerify,
  Str_BehavioralVerifyTip,
  Str_captchaError,
  Str_Confirm,
  Str_enterCaptcha,
  Str_Refresh,
  Str_unknownError,
} from "../../language/string_res";
import { showDialog } from "../../components/CommonDialog";
import FastImage from "react-native-fast-image";
import {
  color_333333,
  color_main,
  ColorF6F6F6,
} from "../../assets/style/colors";
import { getLanguage } from "../../language/i18n";
// import {
//   DraxDragWithReceiverEventData,
//   DraxProvider,
//   DraxView,
// } from "react-native-drax";
import { ImageView } from "../../components/ImageView";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { CommonInput } from "./CommonInput";
import { isIOS } from "../../utils/utils";
import { useWindowDimensions } from "react-native";
type BehavioralVerifyModalProps = {
  visible: boolean;
  onResult: BehavioralVerifyCallbackType;
};

export type BehavioralVerifyCallbackType = (
  isSuccess: boolean,
  token: string
) => void;

const NUM_COLUMNS = 4;
const BOX_PADDING = 16.0;
const isUseNewCaptcha = false; // 是否使用新版拼图验证码

export const BehavioralVerifyModal = (props: BehavioralVerifyModalProps) => {
  const { width: screenWidth } = useWindowDimensions();
  // Web 优化：限制最大宽度
  const maxWidth = Platform.OS === 'web' ? 500 : screenWidth;
  const actualWidth = Math.min(screenWidth, maxWidth);
  const gridWidth = actualWidth - BOX_PADDING * 4;
  const itemSize = Math.ceil(gridWidth / NUM_COLUMNS); // 两边各20padding + 中间3个间隙20
  const { visible, onResult } = props;
  const [dataSources, setDataSource] = useState<BehavioralVerifyItem[]>([]);
  const captchaToken = useRef<string>("");

  /// 旧验证码
  const [captcha, setCaptcha] = useState(""); //输入的图形验证码
  const [captchaRes, setCaptchaResponse] = useState<CaptchaResponse>(); //图形验证码
  const [captchaUri, setCaptchaUri] = useState<string | undefined>(); // 图形验证码规范化

  useEffect(() => {
    if (visible) {
      fetchCaptcha();
    }
  }, [visible]);

  function fetchCaptcha() {
    isUseNewCaptcha ? fetchBehavioralVerifyData() : getCaptchaOld();
  }

  function handleError(err: { msg: string }) {
    if (dataSources.length === 0) {
      showDialog({
        message: err.msg || Str_unknownError,
        onConfirm() {
          onResult(false, "");
        },
      });
      return;
    }

    showToast(err.msg || Str_unknownError);
  }

  async function fetchBehavioralVerifyData() {
    setDataSource([]);
    showLoading();
    behavioralVerifyGet(getLanguage())
      .then((res: any) => {
        hideLoading();
        const data: BehavioralVerifyResponse = res.data;
        if (res.code === 0 && data.items && (data.items?.length ?? 0) >= 8) {
          const mapped = data.items.map((item, index) => {
            const newRaw = normalizeCaptchaRaw(item.raw);
            if (newRaw) {
              item.raw = newRaw;
            }

            return item;
          });

          captchaToken.current = data.token;
          setDataSource(mapped);
        } else {
          handleError({ msg: res.msg || Str_unknownError });
        }
      })
      .catch((err) => {
        hideLoading();
        handleError({ msg: err.msg || Str_unknownError });
      });
  }

  function onCommitCaptchaVerifyData() {
    isUseNewCaptcha ? commitBehavioralVerifyData() : onCommitCaptchaCheck();
  }

  async function onCommitCaptchaCheck() {
    const res = await captchaChk(
      getLanguage(),
      captchaRes?.token || "",
      captcha
    );
    // console.log(`----> ${JSON.stringify(res)}`)
    if (res.code !== 0) {
      hideLoading();
      getCaptchaOld();
      showToast(res.msg || Str_captchaError);
      return;
    }

    onResult(true, captchaRes?.token ?? "");
  }

  async function commitBehavioralVerifyData() {
    showLoading();
    const items = dataSources.map((item, index) => {
      return { key: item.key, index: index };
    });
    const body: BehavioralVerifyCheckBody = {
      lang: getLanguage(),
      token: captchaToken.current,
      items: items,
    };

    behavioralVerifyCommit(body)
      .then((res) => {
        hideLoading();
        if (res.code === 0 && captchaToken.current.length > 0) {
          onResult(true, captchaToken.current);
        } else {
          setDataSource([]);
          fetchBehavioralVerifyData();
          showToast(res.msg || Str_unknownError);
        }
      })
      .catch((err) => {
        hideLoading();
        setDataSource([]);
        fetchBehavioralVerifyData();
        showToast(err.msg || Str_unknownError);
      });
  }

  // 处理拖放结束后的位置交换
  //   const handleReceiveDragDrop = (data: DraxDragWithReceiverEventData) => {
  //     const {
  //       dragged: { payload: fromId },
  //       receiver: { payload: toId },
  //     } = data;

  //     setDataSource((prevItems) => {
  //       const newItems = [...prevItems];
  //       const fromIndex = newItems.findIndex((item) => item.key === fromId);
  //       const toIndex = newItems.findIndex((item) => item.key === toId);

  //       if (fromIndex !== -1 && toIndex !== -1) {
  //         // 交换两个项目的位置
  //         [newItems[fromIndex], newItems[toIndex]] = [
  //           newItems[toIndex],
  //           newItems[fromIndex],
  //         ];
  //       }
  //       return newItems;
  //     });
  //   };

  const getCaptchaOld = () => {
    setCaptcha("");
    captchaGet(getLanguage()).then((res) => {
      if (res.code === 0) {
        setCaptchaResponse(res.data);
        console.log(`---> ${JSON.stringify(res.data)}`);
        setCaptchaUri(normalizeCaptchaRaw(res.data?.raw));
      } else {
        showToast(res.msg || "获取图形码失败");
        setCaptchaResponse(undefined);
        setCaptchaUri(undefined);
      }
    });
  };

  // 将 raw 规范化为 RN 可用的 uri
  const normalizeCaptchaRaw = (raw?: string): string | undefined => {
    if (!raw) return undefined;
    if (raw === "null") return undefined;
    if (raw.startsWith("http") || raw.startsWith("data:")) {
      return raw; // 已是完整 url 或 data-uri
    }
    return `data:image/png;base64,${raw}`;
  };

  const oldCaptchaComp = () => {
    return (
      <View style={styles.mask}>
        <View
          style={[
            styles.container,
            {
              width: actualWidth - 2 * BOX_PADDING,
              marginBottom: Platform.OS === 'web' ? 0 : 80,
              maxWidth: Platform.OS === 'web' ? 450 : undefined,
            },
          ]}
        >
          {closeBtn()}
          {headerView()}
          <View style={[
            { marginTop: 12, flexDirection: "row" },
            Platform.OS === 'web' && styles.inputRow,
          ]}>
            <CommonInput
              style={ { flex: 1, paddingStart: 0 ,minWidth: 0} }
              placeholder={Str_enterCaptcha}
              text={captcha}
              onChangeText={(text) => {
                setCaptcha(text);
              }}
              keyboardType={isIOS ? "ascii-capable" : "default"}
                        //   autoComplete="off"
                        //   autoCorrect={false}
            />
            {captchaUri ? (
              <ImageView
                onClick={getCaptchaOld}
                style={{
                  width: "40%",
                  height: 48,
                  marginStart: 12,
                  borderRadius: 4,
                  backgroundColor: ColorF6F6F6,
                }}
                imgStyle={{
                  width: "100%",
                  height: "100%",
                  resizeMode: "contain",
                  borderRadius: 4,
                }}
                source={{ uri: captchaUri }}
              />
            ) : (
              <View
                style={{
                  width: "40%",
                  height: 48,
                  marginStart: 12,
                  backgroundColor: "#eef2f9",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 4,
                }}
              >
                <Text style={{ fontSize: 12, color: "#999" }}>加载中</Text>
              </View>
            )}
          </View>
          {footerView()}
        </View>
      </View>
    );
  };

  const newCaptchaComp = () => {
    return (
      <GestureHandlerRootView style={{ flex: 1 }}>
        {/* <View style={styles.mask}>
          <View
            style={[
              styles.container,
              {
                width: actualWidth - 2 * BOX_PADDING,
                maxWidth: Platform.OS === 'web' ? 450 : undefined,
              },
            ]}
          >
            {closeBtn()}
            {headerView()}
            <View
              style={[
                styles.gridWrapper,
                { width: itemSize * NUM_COLUMNS, height: itemSize * 2 },
              ]}
            >
              <DraxProvider
                style={{
                  width: itemSize * NUM_COLUMNS,
                  height: itemSize * 2,
                  alignContent: "center",
                }}
              >
                <View style={[styles.grid, { width: itemSize * NUM_COLUMNS }]}>
                  {dataSources.map((item) => (
                    // 2. 每个格子既是可拖拽项，也是可接收放置的区域[citation:3]
                    <DraxView
                      key={item.key}
                      style={[
                        styles.draggableBox,
                        { width: itemSize, height: itemSize },
                      ]}
                      // 携带自身 id 作为数据
                      payload={item.key}
                      // 长按激活拖拽的视觉反馈 - Android 需要更长的延迟
                      longPressDelay={Platform.OS === "android" ? 500 : 300}
                      // 拖动时的样式
                      draggingStyle={styles.dragging}
                      hoverStyle={[
                        styles.draggableBox,
                        { width: itemSize, height: itemSize },
                      ]}
                      hoverDragReleasedStyle={styles.hoverReleased}
                      // 拖拽释放时，在接收器上触发交换
                      onReceiveDragDrop={handleReceiveDragDrop}
                      // 添加拖拽开始回调，确保 Android 上能正确识别
                      onDragStart={() => {
                        // 拖拽开始时可以添加反馈
                      }}
                      // Android 上可能需要禁用某些默认行为
                      onDragEnd={() => {
                        // 拖拽结束回调
                      }}
                    >
                      <FastImage
                        source={{
                          uri: item.raw,
                          priority: FastImage.priority.normal,
                          cache: FastImage.cacheControl.immutable,
                        }}
                        style={styles.image}
                        resizeMode={FastImage.resizeMode.cover}
                      />
                    </DraxView>
                  ))}
                </View>
              </DraxProvider>
            </View>
            {footerView()}
          </View>
        </View> */}
      </GestureHandlerRootView>
    );
  };

  const headerView = () => {
    return (
      <View style={{ marginTop: 5 }}>
        <Text style={styles.title}>{Str_BehavioralVerify}</Text>
        <Text style={styles.subTitle}>
          {isUseNewCaptcha ? Str_BehavioralVerifyTip : Str_enterCaptcha}
        </Text>
      </View>
    );
  };

  const footerView = () => {
    return (
      <View style={styles.footer}>
        {isUseNewCaptcha && (
          <TouchableOpacity
            style={styles.refreshBtn}
            onPress={fetchBehavioralVerifyData}
          >
            <Text style={styles.refreshText}>{Str_Refresh}</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={[
            styles.confirmBtn,
            isUseNewCaptcha
              ? dataSources.length < 8
                ? styles.confirmBtnDisabled
                : { marginLeft: 8 }
              : { height: 50 },
          ]}
          onPress={onCommitCaptchaVerifyData}
          disabled={isUseNewCaptcha ? dataSources.length < 8 : false}
        >
          <Text style={styles.confirmText}>{Str_Confirm}</Text>
        </TouchableOpacity>
      </View>
    );
  };

  const closeBtn = () => {
    return (
      <ImageView
        style={{ padding: 5, position: "absolute", end: 5, top: 10 }}
        source={require("../../assets/image/icon_close.png")}
        onClick={() => onResult(false, "")}
        imgStyle={{ width: 20, height: 20 }}
      />
    );
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      statusBarTranslucent={true}
      visible={visible}
      onRequestClose={() => onResult(false, "")}
    >
      {isUseNewCaptcha ? newCaptchaComp() : oldCaptchaComp()}
    </Modal>
  );
};

const styles = StyleSheet.create({
  mask: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.6)",
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: BOX_PADDING,
    flexDirection: "column",
    justifyContent: "flex-start",
    ...(Platform.OS === 'web' && {
      maxWidth: 90,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    }),
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
    color: "#111",
  },
  subTitle: {
    marginTop: 5,
    color: "#666",
    fontSize: 14,
  },
  gridWrapper: {
    backgroundColor: "#f7f7f7",
    flexDirection: "column",
    marginTop: 10,
    borderRadius: 4,
    overflow: "hidden",
  },

  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  draggableBox: {
    backgroundColor: "#e9e9e9",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  dragging: {
    // 被拖动时的视觉效果
    opacity: 0.3,
    shadowColor: "#007AFF",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    transform: [{ scale: 1.05 }], // 轻微放大
  },
  hoverReleased: {
    opacity: 0, // 核心：将不透明度设为0，使其不可见
    display: "none", // 更彻底的隐藏，防止其拦截触摸事件
  },
  image: {
    width: "100%",
    height: "100%",
    resizeMode: "cover",
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 20,
  },
  refreshBtn: {
    flex: 1,
    height: 50,
    marginRight: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    alignItems: "center",
    justifyContent: "center",
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      userSelect: 'none',
    }),
  },
  confirmBtn: {
    flex: 1,
    height: 50,
    borderRadius: 8,
    backgroundColor: color_main,
    alignItems: "center",
    justifyContent: "center",
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      userSelect: 'none',
    }),
  },
  confirmBtnDisabled: {
    backgroundColor: "#9db5ff",
  },
  refreshText: {
    color: color_333333,
    fontSize: 16,
    fontWeight: "500",
  },
  confirmText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  // Web 优化样式
  inputRow: {
    ...(Platform.OS === 'web' && {
      gap: 12,
      flexWrap: 'nowrap',
    }),
  },
});
